import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import ExportMenu from '../../../components/orizon-ui/ExportMenu';
import { useToast } from '../../../components/orizon-ui/Toast';
import {
  PALETTE, coloreCategoria, AXIS_STYLE, GRID_PROPS, fmt, meseBreve,
  GraficoTooltip, KpiCard, ChartCard, ChartToggle, FilterGroup, LegendaPills,
} from '../../../components/orizon-ui/Grafici';
import { esportaCSV, esportaXLSX, esportaPDF } from '../../../components/gru/esporta';
import { gruStatistiche, gruOpzioni, mapGruError } from '../../../lib/services/gruService';
import GraficoDistribuzione from './GraficoDistribuzione';

const TUTTO = 'TUTTO';
const collator = new Intl.Collator('it', { numeric: true, sensitivity: 'base' });

const elencoUnico = (valori) => [...new Set(valori.filter((v) => v !== null && v !== undefined && v !== ''))];

const conTutti = (valori, etichetta = (v) => String(v)) => [
  { value: TUTTO, label: 'Tutti' },
  ...valori.map((v) => ({ value: String(v), label: etichetta(v) })),
];

const chiaveMese = (r) => `${String(r.meseNum).padStart(2, '0')}/${r.anno}`;

/**
 * Dashboard statistiche GRU.
 * Dati: gru_v_stat_mensile (aggregata per mese × gru × reparto × causa × tipo × articolo, senza interventi annullati).
 * Si carica una volta sola; filtri e grafici si calcolano subito nel browser.
 */
const DashboardStatistiche = () => {
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [troncato, setTroncato] = useState(false);
  const [esportando, setEsportando] = useState(false);

  const [annoSel, setAnnoSel] = useState(TUTTO);
  const [gruSel, setGruSel] = useState(TUTTO);
  const [repartoSel, setRepartoSel] = useState(TUTTO);
  const [tipoSel, setTipoSel] = useState(TUTTO);
  const [causaSel, setCausaSel] = useState(TUTTO);
  const [articoloSel, setArticoloSel] = useState(TUTTO);

  const [tipoTrend, setTipoTrend] = useState('area'); // area | bar | line
  const [tipoAnno, setTipoAnno] = useState('bar'); // bar | line

  const primoCaricamento = useRef(true);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const [{ righe, troncato: incompleto }, elencoTipi] = await Promise.all([gruStatistiche(), gruOpzioni('tipi')]);
      setTipi(elencoTipi);
      setTroncato(incompleto);
      const normalizzate = righe.map((r) => ({
        mese: r.mese,
        anno: Number(r.anno),
        meseNum: Number(r.mese_num),
        gruId: r.gru_id,
        gru: r.gru_codice,
        repartoId: r.reparto_id,
        reparto: r.reparto_label,
        causaId: r.causa_id,
        causa: r.causa_label || 'Senza causa',
        tipoKey: r.tipo_key,
        tipoLabel: elencoTipi.find((t) => t.id === r.tipo_id)?.label ?? r.tipo_key,
        articoloId: r.articolo_id,
        ean: r.ean,
        nome: r.nome,
        n: Number(r.n_interventi),
        qta: Number(r.qta_totale),
      }));
      setRows(normalizzate);

      // al primo caricamento si parte dalle sostituzioni (il dato più significativo); se non ce ne sono, tutti i tipi
      if (primoCaricamento.current) {
        primoCaricamento.current = false;
        if (normalizzate.some((r) => r.tipoKey === 'sostituzione')) setTipoSel('sostituzione');
      }
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  // ─── Elenchi per i filtri (dall'insieme completo dei dati) ─────────────────
  const anni = useMemo(() => elencoUnico(rows.map((r) => r.anno)).sort((a, b) => b - a), [rows]);
  const gruAll = useMemo(() => elencoUnico(rows.map((r) => r.gru)).sort(collator.compare), [rows]);
  const repartiAll = useMemo(() => elencoUnico(rows.map((r) => r.reparto)).sort(collator.compare), [rows]);
  const causeAll = useMemo(() => elencoUnico(rows.map((r) => r.causa)).sort(collator.compare), [rows]);
  const tipiAll = useMemo(() => {
    const mappa = new Map();
    rows.forEach((r) => mappa.set(r.tipoKey, r.tipoLabel));
    return [...mappa.entries()].map(([value, label]) => ({ value, label }));
  }, [rows]);
  const articoliAll = useMemo(() => {
    const mappa = new Map();
    rows.forEach((r) => mappa.set(r.articoloId, `${r.nome} (${r.ean})`));
    return [...mappa.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [rows]);

  // ─── Filtraggio ────────────────────────────────────────────────────────────
  const passa = useCallback((r, escludi = []) => {
    if (!escludi.includes('anno') && annoSel !== TUTTO && r.anno !== Number(annoSel)) return false;
    if (gruSel !== TUTTO && r.gru !== gruSel) return false;
    if (repartoSel !== TUTTO && r.reparto !== repartoSel) return false;
    if (tipoSel !== TUTTO && r.tipoKey !== tipoSel) return false;
    if (causaSel !== TUTTO && r.causa !== causaSel) return false;
    if (articoloSel !== TUTTO && r.articoloId !== articoloSel) return false;
    return true;
  }, [annoSel, gruSel, repartoSel, tipoSel, causaSel, articoloSel]);

  const rowsF = useMemo(() => rows.filter((r) => passa(r)), [rows, passa]);
  const rowsSenzaAnno = useMemo(() => rows.filter((r) => passa(r, ['anno'])), [rows, passa]);

  const nomeTipo = tipiAll.find((t) => t.value === tipoSel)?.label;
  const nomeArticolo = articoliAll.find((a) => a.value === articoloSel)?.label;
  const contextLabel = [
    annoSel !== TUTTO ? annoSel : null,
    gruSel !== TUTTO ? gruSel : null,
    repartoSel !== TUTTO ? repartoSel : null,
    tipoSel !== TUTTO ? nomeTipo : null,
    causaSel !== TUTTO ? causaSel : null,
    articoloSel !== TUTTO ? nomeArticolo : null,
  ].filter(Boolean).join(' · ') || null;

  const haFiltri = [annoSel, gruSel, repartoSel, tipoSel, causaSel, articoloSel].some((v) => v !== TUTTO);
  const azzera = () => {
    setAnnoSel(TUTTO);
    setGruSel(TUTTO);
    setRepartoSel(TUTTO);
    setTipoSel(TUTTO);
    setCausaSel(TUTTO);
    setArticoloSel(TUTTO);
  };

  // ─── Aggregazioni ──────────────────────────────────────────────────────────
  const totale = useMemo(() => rowsF.reduce((s, r) => s + r.n, 0), [rowsF]);
  const gruVis = useMemo(() => elencoUnico(rowsF.map((r) => r.gru)).sort(collator.compare), [rowsF]);

  const distribuzione = useCallback((chiaveRiga, elencoCompleto) => {
    const mappa = {};
    rowsF.forEach((r) => {
      const k = r[chiaveRiga];
      mappa[k] = (mappa[k] ?? 0) + r.n;
    });
    return Object.entries(mappa)
      .map(([k, valore]) => ({ chiave: k, label: k, valore, colore: coloreCategoria(k, elencoCompleto) }))
      .sort((a, b) => b.valore - a.valore);
  }, [rowsF]);

  const datiGru = useMemo(() => distribuzione('gru', gruAll), [distribuzione, gruAll]);
  const datiReparto = useMemo(() => distribuzione('reparto', repartiAll), [distribuzione, repartiAll]);
  const datiCausa = useMemo(() => distribuzione('causa', causeAll), [distribuzione, causeAll]);

  const datiTrend = useMemo(() => {
    const mappa = {};
    rowsF.forEach((r) => {
      const k = chiaveMese(r);
      if (!mappa[k]) mappa[k] = { _key: k, _ord: r.anno * 100 + r.meseNum };
      mappa[k][r.gru] = (mappa[k][r.gru] ?? 0) + r.n;
    });
    return Object.values(mappa)
      .sort((a, b) => a._ord - b._ord)
      .map((d) => ({ ...d, mese: meseBreve(d._key) }));
  }, [rowsF]);

  const topArticoli = useMemo(() => {
    const mappa = {};
    rowsF.forEach((r) => {
      if (!mappa[r.articoloId]) mappa[r.articoloId] = { etichetta: r.nome, ean: r.ean, valore: 0 };
      mappa[r.articoloId].valore += r.n;
    });
    return Object.values(mappa).sort((a, b) => b.valore - a.valore).slice(0, 10);
  }, [rowsF]);

  // confronto annuale: sempre tutti gli anni (ignora il filtro anno) per poterli paragonare
  const anniAsc = useMemo(() => [...anni].sort((a, b) => a - b), [anni]);
  const gruConDati = useMemo(
    () => gruAll.filter((g) => rowsSenzaAnno.some((r) => r.gru === g)),
    [gruAll, rowsSenzaAnno],
  );
  const datiAnno = useMemo(() => anniAsc.map((a) => {
    const obj = { anno: String(a) };
    gruConDati.forEach((g) => {
      obj[g] = rowsSenzaAnno.filter((r) => r.anno === a && r.gru === g).reduce((s, r) => s + r.n, 0);
    });
    return obj;
  }), [anniAsc, gruConDati, rowsSenzaAnno]);

  // KPI
  const articoliDistinti = useMemo(() => new Set(rowsF.map((r) => r.articoloId)).size, [rowsF]);
  const gruTop = datiGru[0];
  const mediaMese = datiTrend.length ? (totale / datiTrend.length).toFixed(1) : '0';
  const variazioneAnnua = useMemo(() => {
    if (anni.length < 2) return null;
    const conta = (a) => rowsSenzaAnno.filter((r) => r.anno === a).reduce((s, r) => s + r.n, 0);
    const corrente = conta(anni[0]);
    const precedente = conta(anni[1]);
    if (!precedente) return null;
    return Math.round(((corrente - precedente) / precedente) * 100);
  }, [anni, rowsSenzaAnno]);

  // tabella riepilogo gru × anno
  const anniTabella = annoSel === TUTTO ? anniAsc : [Number(annoSel)];

  // ─── Esportazione dei dati aggregati filtrati ──────────────────────────────
  const COLONNE_EXPORT = [
    { label: 'Mese', valore: (r) => chiaveMese(r), larghezza: 10, align: 'center' },
    { label: 'Gru', valore: (r) => r.gru, larghezza: 12, align: 'center' },
    { label: 'Reparto', valore: (r) => r.reparto, larghezza: 14, align: 'center' },
    { label: 'Tipo', valore: (r) => r.tipoLabel, larghezza: 14, align: 'center' },
    { label: 'Causa', valore: (r) => r.causa, larghezza: 22, align: 'left' },
    { label: 'Codice', valore: (r) => r.ean, larghezza: 16, align: 'left' },
    { label: 'Articolo', valore: (r) => r.nome, larghezza: 38, align: 'left' },
    { label: 'Interventi', valore: (r) => r.n, larghezza: 11, align: 'center' },
    { label: 'Quantità', valore: (r) => r.qta, larghezza: 11, align: 'center' },
  ];

  const esporta = async (formato) => {
    if (rowsF.length === 0) {
      toast.warn('Nessun dato da esportare.');
      return;
    }
    setEsportando(true);
    try {
      const base = { nomeFile: 'statistiche_gru', colonne: COLONNE_EXPORT, righe: rowsF };
      if (formato === 'csv') esportaCSV(base);
      else if (formato === 'xlsx') await esportaXLSX({ ...base, nomeFoglio: 'Statistiche GRU' });
      else {
        await esportaPDF({
          ...base,
          nomeFile: 'Statistiche_GRU',
          titolo: 'STATISTICHE INTERVENTI GRU',
          sottotitolo: contextLabel ? `Filtri — ${contextLabel}` : '',
        });
      }
      toast.success(`Esportate ${rowsF.length} righe.`);
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setEsportando(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full animate-in fade-in duration-700">
      {/* Intestazione */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6 px-1">
        <div>
          <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
            STATISTICHE<span className="text-primary">GRU</span>
          </h2>
          <div className="flex items-center gap-2 mt-2 md:mt-3">
            <span className="h-[2px] w-6 bg-primary rounded-full" />
            <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">
              Analisi interventi per gru, reparto, causa e articolo
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          {haFiltri && (
            <button
              type="button"
              onClick={azzera}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500/80 hover:bg-red-500/20 text-[10px] font-black uppercase tracking-widest transition-all"
            >
              <Icon name="FilterX" size={12} />
              Reset filtri
            </button>
          )}
          <ExportMenu onEsporta={esporta} inCorso={esportando} disabilitato={loading || rowsF.length === 0} />
          <button
            type="button"
            onClick={carica}
            disabled={loading}
            className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all duration-200 shadow-sm disabled:opacity-50"
          >
            <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Aggiorna</span>
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="p-5 rounded-3xl bg-muted/[0.06] border border-border/30 flex flex-col gap-3 mb-6">
        <div className="flex flex-wrap gap-x-7 gap-y-2.5">
          {anni.length > 0 && (
            <FilterGroup label="Anno" options={conTutti(anni)} value={annoSel} onChange={setAnnoSel} color={PALETTE[0]} />
          )}
          {gruAll.length > 0 && (
            <FilterGroup label="Gru" options={conTutti(gruAll)} value={gruSel} onChange={setGruSel} color={PALETTE[2]} />
          )}
          {repartiAll.length > 0 && (
            <FilterGroup label="Rep." options={conTutti(repartiAll)} value={repartoSel} onChange={setRepartoSel} color={PALETTE[1]} />
          )}
          {tipiAll.length > 0 && (
            <FilterGroup label="Tipo" options={[{ value: TUTTO, label: 'Tutti' }, ...tipiAll]} value={tipoSel} onChange={setTipoSel} color={PALETTE[4]} />
          )}
          {causeAll.length > 0 && (
            <FilterGroup label="Causa" options={conTutti(causeAll)} value={causaSel} onChange={setCausaSel} color={PALETTE[5]} />
          )}
        </div>

        {articoliAll.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/30 shrink-0 min-w-[40px]">Artic.</span>
            <select
              value={articoloSel}
              onChange={(e) => setArticoloSel(e.target.value)}
              aria-label="Filtra per articolo"
              className="max-w-full sm:max-w-md bg-muted/40 border border-border/40 rounded-xl px-3 py-1.5 text-[11px] font-bold text-foreground/80 outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/10 transition-all cursor-pointer"
            >
              <option value={TUTTO}>Tutti gli articoli</option>
              {articoliAll.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
        )}

        {haFiltri && (
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
            <span className="text-[9px] font-black uppercase tracking-widest text-primary/50">Filtro attivo: {contextLabel}</span>
          </div>
        )}
      </div>

      {troncato && (
        <p className="mb-4 px-1 text-[10px] font-black uppercase tracking-widest text-amber-500">
          Attenzione: i dati sono molti e sono stati letti solo in parte. Restringi il periodo o contatta l'amministratore.
        </p>
      )}

      {loading ? (
        <div className="flex flex-col gap-4">
          {[110, 280, 240, 240].map((h, i) => (
            <div key={i} className="rounded-3xl bg-muted/20 animate-pulse" style={{ height: h }} />
          ))}
        </div>
      ) : rowsF.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-28 gap-4">
          <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
            <Icon name="BarChart2" size={28} className="text-muted-foreground/15" />
          </div>
          <span className="text-sm font-black uppercase tracking-widest text-muted-foreground/25 text-center">
            {rows.length === 0 ? 'Nessun intervento registrato' : 'Nessun dato per i filtri selezionati'}
          </span>
          {haFiltri && (
            <button type="button" onClick={azzera} className="text-[10px] font-black uppercase tracking-widest text-primary/60 hover:text-primary transition-colors">
              Azzera filtri
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Totale interventi" value={fmt(totale)} sub={contextLabel ?? 'storico completo'} icon="Wrench" color={PALETTE[0]} trend={variazioneAnnua} />
            <KpiCard label="Articoli distinti" value={fmt(articoliDistinti)} sub="articoli diversi coinvolti" icon="Package" color={PALETTE[5]} />
            <KpiCard label="Gru più attiva" value={gruTop ? gruTop.label : '—'} sub={gruTop ? `${fmt(gruTop.valore)} interventi` : ''} icon="Zap" color={PALETTE[1]} />
            <KpiCard label="Media / mese" value={mediaMese} sub="interventi medi mensili" icon="Calendar" color={PALETTE[4]} />
          </div>

          {/* Andamento mensile */}
          {datiTrend.length > 0 && (
            <ChartCard
              title="Andamento mensile"
              subtitle={`${datiTrend.length} mesi — interventi per gru`}
              icon="TrendingUp"
              contextLabel={contextLabel}
              actions={(
                <ChartToggle value={tipoTrend} onChange={setTipoTrend} options={[
                  { value: 'area', icon: 'AreaChart', label: 'Area' },
                  { value: 'bar', icon: 'BarChart2', label: 'Bar' },
                  { value: 'line', icon: 'TrendingUp', label: 'Line' },
                ]}
                />
              )}
            >
              <ResponsiveContainer width="100%" height={260}>
                {tipoTrend === 'bar' ? (
                  <BarChart data={datiTrend} margin={{ top: 5, right: 5, left: -18, bottom: 0 }} barGap={2} barCategoryGap="30%">
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <Tooltip content={<GraficoTooltip />} cursor={{ fill: 'rgba(128,128,128,0.05)' }} />
                    {gruVis.map((g) => (
                      <Bar key={g} dataKey={g} name={g} fill={coloreCategoria(g, gruAll)} radius={[4, 4, 0, 0]} maxBarSize={28} />
                    ))}
                  </BarChart>
                ) : tipoTrend === 'line' ? (
                  <LineChart data={datiTrend} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <Tooltip content={<GraficoTooltip />} cursor={{ stroke: 'rgba(128,128,128,0.15)', strokeWidth: 1 }} />
                    {gruVis.map((g) => (
                      <Line
                        key={g} type="monotone" dataKey={g} name={g}
                        stroke={coloreCategoria(g, gruAll)} strokeWidth={2.5}
                        dot={{ r: 3, fill: coloreCategoria(g, gruAll), strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 0 }} connectNulls
                      />
                    ))}
                  </LineChart>
                ) : (
                  <AreaChart data={datiTrend} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                    <defs>
                      {gruVis.map((g) => {
                        const col = coloreCategoria(g, gruAll);
                        return (
                          <linearGradient key={g} id={`gru-ag-${gruAll.indexOf(g)}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={col} stopOpacity={0.22} />
                            <stop offset="100%" stopColor={col} stopOpacity={0.01} />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <Tooltip content={<GraficoTooltip />} cursor={{ stroke: 'rgba(128,128,128,0.12)', strokeWidth: 1 }} />
                    {gruVis.map((g) => (
                      <Area
                        key={g} type="monotone" dataKey={g} name={g}
                        stroke={coloreCategoria(g, gruAll)} strokeWidth={2.5}
                        fill={`url(#gru-ag-${gruAll.indexOf(g)})`} fillOpacity={1}
                        connectNulls dot={false}
                        activeDot={{ r: 4, strokeWidth: 0, fill: coloreCategoria(g, gruAll) }}
                      />
                    ))}
                  </AreaChart>
                )}
              </ResponsiveContainer>
              <LegendaPills voci={gruVis.map((g) => ({
                chiave: g,
                label: g,
                colore: coloreCategoria(g, gruAll),
                totale: datiTrend.reduce((s, d) => s + (d[g] || 0), 0),
              }))}
              />
            </ChartCard>
          )}

          {/* Per gru + Top articoli */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GraficoDistribuzione
              titolo="Distribuzione per gru"
              sottotitolo="Interventi totali per gru"
              icona="Construction"
              contextLabel={contextLabel}
              dati={datiGru}
              totale={totale}
            />

            {topArticoli.length > 0 && (
              <ChartCard title="Top 10 articoli" subtitle="più frequentemente coinvolti" icon="Package" contextLabel={contextLabel}>
                <ResponsiveContainer width="100%" height={topArticoli.length * 30 + 20} minHeight={200}>
                  <BarChart data={topArticoli} layout="vertical" margin={{ top: 0, right: 45, left: 0, bottom: 0 }} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.07)" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis
                      type="category"
                      dataKey="etichetta"
                      width={130}
                      tick={{ fontSize: 9, fontWeight: 700, fill: 'rgba(128,128,128,0.55)' }}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tickFormatter={(v) => (String(v).length > 22 ? `${String(v).slice(0, 21)}…` : v)}
                    />
                    <Tooltip content={<GraficoTooltip />} cursor={{ fill: 'rgba(128,128,128,0.05)' }} />
                    <Bar dataKey="valore" name="Interventi" radius={[0, 7, 7, 0]} maxBarSize={16}>
                      {topArticoli.map((_, i) => <Cell key={i} fill={`rgba(99,102,241,${0.95 - i * 0.08})`} />)}
                      <LabelList dataKey="valore" position="right" style={{ fontSize: 10, fontWeight: 900, fill: 'rgba(99,102,241,0.7)' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>

          {/* Per reparto + per causa */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GraficoDistribuzione
              titolo="Distribuzione per reparto"
              sottotitolo="Interventi per reparto (FUNI, MOTORI, ...)"
              icona="Layers"
              contextLabel={contextLabel}
              dati={datiReparto}
              totale={totale}
            />
            <GraficoDistribuzione
              titolo="Distribuzione per causa"
              sottotitolo="Perché si interviene"
              icona="AlertTriangle"
              contextLabel={contextLabel}
              dati={datiCausa}
              totale={totale}
            />
          </div>

          {/* Confronto annuale */}
          {datiAnno.length > 0 && gruConDati.length > 0 && (
            <ChartCard
              title="Confronto annuale per gru"
              subtitle="interventi per anno — tutti gli anni (ignora il filtro anno)"
              icon="Calendar"
              contextLabel={contextLabel}
              actions={(
                <ChartToggle value={tipoAnno} onChange={setTipoAnno} options={[
                  { value: 'bar', icon: 'BarChart2', label: 'Bar' },
                  { value: 'line', icon: 'TrendingUp', label: 'Line' },
                ]}
                />
              )}
            >
              <ResponsiveContainer width="100%" height={230}>
                {tipoAnno === 'line' ? (
                  <LineChart data={datiAnno} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="anno" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <Tooltip content={<GraficoTooltip />} />
                    {gruConDati.map((g) => (
                      <Line
                        key={g} type="monotone" dataKey={g} name={g}
                        stroke={coloreCategoria(g, gruAll)} strokeWidth={2.5}
                        dot={{ r: 5, fill: coloreCategoria(g, gruAll), strokeWidth: 0 }}
                        activeDot={{ r: 7, strokeWidth: 0 }} connectNulls
                      />
                    ))}
                  </LineChart>
                ) : (
                  <BarChart data={datiAnno} margin={{ top: 5, right: 5, left: -18, bottom: 0 }} barCategoryGap="30%" barGap={2}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="anno" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                    <Tooltip content={<GraficoTooltip />} cursor={{ fill: 'rgba(128,128,128,0.05)' }} />
                    {gruConDati.map((g) => (
                      <Bar key={g} dataKey={g} name={g} fill={coloreCategoria(g, gruAll)} radius={[5, 5, 0, 0]} maxBarSize={35} />
                    ))}
                  </BarChart>
                )}
              </ResponsiveContainer>
              <LegendaPills voci={gruConDati.map((g) => ({
                chiave: g,
                label: g,
                colore: coloreCategoria(g, gruAll),
                totale: datiAnno.reduce((s, d) => s + (d[g] || 0), 0),
              }))}
              />
            </ChartCard>
          )}

          {/* Riepilogo gru × anno */}
          {gruVis.length > 0 && anniTabella.length > 0 && (
            <ChartCard title="Riepilogo gru × anno" subtitle="numero di interventi" icon="Table" contextLabel={contextLabel}>
              <div className="overflow-x-auto rounded-2xl border border-border/20">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-muted/30">
                      <th className="px-4 py-3 text-left text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20 whitespace-nowrap">Gru</th>
                      {anniTabella.map((a) => (
                        <th key={a} className="px-4 py-3 text-center text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20">{a}</th>
                      ))}
                      <th className="px-4 py-3 text-center text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20">Tot.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gruVis.map((g, gi) => {
                      const col = coloreCategoria(g, gruAll);
                      const conteggi = anniTabella.map((a) => rowsF.filter((r) => r.gru === g && r.anno === a).reduce((s, r) => s + r.n, 0));
                      const massimo = Math.max(...conteggi, 1);
                      const totG = rowsF.filter((r) => r.gru === g).reduce((s, r) => s + r.n, 0);
                      return (
                        <tr key={g} className={`border-b border-border/10 last:border-0 ${gi % 2 === 0 ? '' : 'bg-muted/[0.03]'}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: col }} />
                              <span className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap" style={{ color: col }}>{g}</span>
                            </div>
                          </td>
                          {conteggi.map((cnt, ci) => {
                            const intensita = cnt / massimo;
                            const hex = Math.round(intensita * 55 + 12).toString(16).padStart(2, '0');
                            return (
                              <td key={ci} className="px-4 py-2.5 text-center">
                                {cnt > 0 ? (
                                  <span className="inline-block px-2.5 py-1 rounded-lg text-[10px] font-black tabular-nums" style={{ background: `${col}${hex}`, color: col }}>
                                    {cnt}
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground/15 font-bold">—</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-4 py-2.5 text-center">
                            <span className="inline-block px-3 py-1 rounded-lg text-[10px] font-black tabular-nums" style={{ background: `${col}22`, color: col }}>
                              {totG}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-muted/15 border-t-2 border-border/20">
                      <td className="px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">Totale</td>
                      {anniTabella.map((a) => {
                        const cnt = rowsF.filter((r) => r.anno === a).reduce((s, r) => s + r.n, 0);
                        return (
                          <td key={a} className="px-4 py-2.5 text-center text-[10px] font-black text-foreground/50 tabular-nums">{cnt || '—'}</td>
                        );
                      })}
                      <td className="px-4 py-2.5 text-center">
                        <span className="text-[12px] font-black text-primary tabular-nums">{totale}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardStatistiche;
